import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { usePostHog } from "@posthog/react";
import { THEME_KEY, hostThemeTokens, readStoredTheme, themeTokens } from "../theme";
import {
  beginGoogleSignIn,
  clearHostSession,
  getStoredHostSession,
} from "../webAuth";
import { fetchAccount, openBillingPortal, type Account } from "../billing";
import { PRICES } from "../config/plans";
import {
  FIRST_MONTH,
  LivePresentation,
  UsagePresentation,
  UsageReport,
  dayLabel,
  durationLabel,
  fetchLive,
  fetchUsage,
  formatResetDate,
  minutesBetween,
  monthKey,
  monthLabel,
  presentationMinutes,
  shiftMonth,
  timeLabel,
} from "../usage";
import logo from "../assets/copresent-logo.svg";

// The signed-in host's home: what's live, how they've been presenting, their
// history, billing and profile. It sits at the root of the clicker — the host
// controller's Home tab leads here — and signed-out visitors still get the
// copresenter join page at "/" (see pages/dashboard.tsx).
//
// Every number here comes from the same session records the Free allowance
// counts (GET /host/usage), so the dashboard can never disagree with the limit.

export type DashboardSection = "home" | "history" | "billing" | "profile";

// --- Shell -------------------------------------------------------------------

const Page = styled.div<{ $dark: boolean }>`
  ${(p) => themeTokens(p.$dark)}
  ${(p) => hostThemeTokens(p.$dark)}
  min-height: 100dvh;
  width: 100%;
  display: flex;
  background: var(--bg-page);
  color: var(--text-primary);

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.nav`
  flex: 0 0 232px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 14px;
  border-right: 1px solid var(--border-soft);
  position: sticky;
  top: 0;
  height: 100dvh;

  /* Phones: a bottom tab bar, like the host controller's — four equal tabs,
     icon over label, within thumb reach. */
  @media (max-width: 767px) {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    height: auto;
    flex-direction: row;
    gap: 0;
    padding: 6px 6px calc(6px + env(safe-area-inset-bottom, 0px));
    border-right: none;
    border-top: 1px solid var(--border-soft);
    background: var(--bg-page);
  }
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 10px 18px;
  font-weight: 700;
  font-size: 16px;
  color: var(--text-headline);
  text-decoration: none;
  img {
    width: 30px;
    height: 30px;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const NavItem = styled(Link)<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 9px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  color: ${(p) => (p.$active ? "var(--link)" : "var(--text-secondary)")};
  background: ${(p) => (p.$active ? "var(--bg-chip)" : "transparent")};

  &:hover {
    color: var(--link);
  }

  svg {
    flex: none;
  }

  @media (max-width: 767px) {
    flex: 1 1 0;
    flex-direction: column;
    gap: 3px;
    padding: 6px 2px;
    font-size: 11px;
  }
`;

// Pinned to the bottom of the sidebar: the upgrade button, then who's signed in.
// The plan and what's left live in the avatar menu, not here.
const SidebarFoot = styled.div`
  margin-top: auto;

  @media (max-width: 767px) {
    display: none;
  }
`;

const NavFoot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding: 16px 10px 10px;
  border-top: 1px solid var(--border-soft);
  font-size: 13px;
  color: var(--text-muted);
  overflow: hidden;

  @media (max-width: 767px) {
    display: none;
  }
`;

const Main = styled.main`
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
  padding: 32px 40px 64px;

  @media (max-width: 767px) {
    /* Clears the fixed tab bar. */
    padding: 20px 16px calc(96px + env(safe-area-inset-bottom, 0px));
  }
`;

const Content = styled.div`
  max-width: 880px;
`;

const Title = styled.h1`
  margin: 0 0 4px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text-headline);
`;

const Lead = styled.p`
  margin: 0 0 24px;
  font-size: 15px;
  color: var(--text-muted);
`;

const SectionTitle = styled.h2`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin: 32px 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-headline);

  a {
    font-size: 13px;
    font-weight: 600;
    color: var(--link);
    text-decoration: none;
  }
`;

const Card = styled.section`
  padding: 18px 20px;
  border-radius: 14px;
  border: 1px solid var(--border-soft);
  background: var(--bg-card);
`;

const Muted = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  color: var(--text-muted);

  a {
    color: var(--link);
    font-weight: 600;
  }
`;

const buttonStyles = css<{ $secondary?: boolean }>`
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  border: 1px solid ${(p) => (p.$secondary ? "var(--border-soft)" : "transparent")};
  background: ${(p) => (p.$secondary ? "transparent" : "var(--link)")};
  color: ${(p) => (p.$secondary ? "var(--text-primary)" : "#fff")};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Button = styled.button<{ $secondary?: boolean }>`
  ${buttonStyles}
`;

const ButtonLink = styled(Link)<{ $secondary?: boolean }>`
  ${buttonStyles}
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

// --- Icons -------------------------------------------------------------------

const ICON = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

const HomeIcon = () => (
  <svg {...ICON}>
    <path d="M3.5 10.5 12 3.5l8.5 7" />
    <path d="M5.5 9v11h13V9" />
    <path d="M10 20v-5.5h4V20" />
  </svg>
);
const ListIcon = () => (
  <svg {...ICON}>
    <rect x="3.5" y="4" width="17" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);
const CardIcon = () => (
  <svg {...ICON}>
    <rect x="2.5" y="5" width="19" height="14" rx="2.2" />
    <path d="M2.5 9.5h19M6.5 15h3" />
  </svg>
);
const UserIcon = () => (
  <svg {...ICON}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20.5c1.3-3.6 4.2-5.5 7.5-5.5s6.2 1.9 7.5 5.5" />
  </svg>
);

const NAV: { id: DashboardSection; label: string; to: string; Icon: () => JSX.Element }[] = [
  { id: "home", label: "Home", to: "/", Icon: HomeIcon },
  { id: "history", label: "History", to: "/history", Icon: ListIcon },
  { id: "billing", label: "Billing", to: "/billing", Icon: CardIcon },
  { id: "profile", label: "Profile", to: "/profile", Icon: UserIcon },
];

const Avatar = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex: none;
`;

const AvatarFallback = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex: none;
  display: grid;
  place-items: center;
  font-size: 13px;
  font-weight: 700;
  background: var(--bg-chip);
  color: var(--text-headline);
`;

const ProfileAvatar = ({ picture, label, size = 28 }: { picture?: string; label: string; size?: number }) => {
  const [failed, setFailed] = useState(false);
  const style = { width: size, height: size };
  return picture && !failed ? (
    // Google's avatar CDN refuses a request carrying our referrer.
    <Avatar src={picture} alt="" style={style} referrerPolicy="no-referrer" onError={() => setFailed(true)} />
  ) : (
    <AvatarFallback style={style} aria-hidden="true">
      {(label || "?").trim().charAt(0).toUpperCase() || "?"}
    </AvatarFallback>
  );
};

// --- Home ----------------------------------------------------------------------

const LiveCard = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  border-color: rgba(34, 197, 94, 0.45);

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const LiveLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #22c55e;

  &::before {
    content: "";
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.18);
  }
`;

const LiveTitle = styled.div`
  margin-top: 6px;
  font-size: 17px;
  font-weight: 700;
  color: var(--text-headline);
  overflow-wrap: anywhere;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Stat = styled(Card)`
  padding: 16px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: var(--text-muted);
`;

const StatValue = styled.div`
  margin-top: 6px;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-headline);
  font-variant-numeric: tabular-nums;
`;

const StatNote = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: var(--text-muted);
`;

const Rows = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  border-radius: 14px;
  border: 1px solid var(--border-soft);
  background: var(--bg-card);
  overflow: hidden;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;

  & + & {
    border-top: 1px solid var(--border-soft);
  }
`;

const RowMain = styled.div`
  min-width: 0;
`;

const RowTitle = styled.div`
  font-weight: 600;
  color: var(--text-headline);
  overflow-wrap: anywhere;
`;

const RowMeta = styled.div`
  margin-top: 3px;
  font-size: 13px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

const totalTime = (minutes: number) =>
  minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;

const HomeView = ({ firstName, plan }: { firstName: string; plan: React.ReactNode }) => {
  const [live, setLive] = useState<LivePresentation[]>([]);
  const [current, setCurrent] = useState<UsageReport | null>(null);
  const [previous, setPrevious] = useState<UsageReport | null>(null);
  const [failed, setFailed] = useState(false);

  // Live state changes under the host's feet (they start the slideshow in
  // another tab), so it's polled; history is read once.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const result = await fetchLive();
      if (!cancelled && Array.isArray(result)) setLive(result);
    };
    void poll();
    const id = window.setInterval(poll, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  useEffect(() => {
    const thisMonth = monthKey(new Date());
    void (async () => {
      const [now, before] = await Promise.all([
        fetchUsage(thisMonth),
        thisMonth > FIRST_MONTH ? fetchUsage(shiftMonth(thisMonth, -1)) : Promise.resolve(null),
      ]);
      if (typeof now === "string") {
        setFailed(true);
        return;
      }
      setCurrent(now);
      setPrevious(before && typeof before !== "string" ? before : null);
    })();
  }, []);

  const stats = useMemo(() => {
    if (!current) return null;
    const presentations = current.presentations;
    const minutes = presentations.reduce((sum, p) => sum + presentationMinutes(p), 0);
    const sessions = presentations.reduce((sum, p) => sum + p.sessions.length, 0);
    const decks = new Set(presentations.map((p) => p.deckId)).size;
    const last = previous?.presentations.length ?? null;
    return { count: presentations.length, minutes, sessions, decks, last };
  }, [current, previous]);

  const recent = useMemo(
    () =>
      [...(current?.presentations ?? []), ...(previous?.presentations ?? [])]
        .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
        .slice(0, 5),
    [current, previous]
  );

  return (
    <>
      <Title>{firstName ? `Hi, ${firstName}` : "Your dashboard"}</Title>
      <Lead>Your presentations with the Copresent remote, at a glance.</Lead>
      {plan}

      {live.map((p) => (
        <LiveCard key={p.deckId} aria-label="Live presentation">
          <div>
            <LiveLabel>Live now</LiveLabel>
            <LiveTitle>{p.title || "Untitled deck"}</LiveTitle>
            {p.totalSlides > 0 && (
              <RowMeta>
                Slide {Math.max(1, p.currentSlide)} of {p.totalSlides}
              </RowMeta>
            )}
          </div>
          <ButtonLink to={`/host/deck/${encodeURIComponent(p.deckId)}`}>
            Return to presentation
          </ButtonLink>
        </LiveCard>
      ))}

      <SectionTitle>This month</SectionTitle>
      {failed && <Muted>We couldn't load your stats just now.</Muted>}
      {!failed && (
        <Stats>
          <Stat>
            <StatLabel>Presentations</StatLabel>
            <StatValue>{stats ? stats.count : "–"}</StatValue>
            <StatNote>
              {stats && stats.last != null ? `${stats.last} last month` : "One deck, one day"}
            </StatNote>
          </Stat>
          <Stat>
            <StatLabel>Time presenting</StatLabel>
            <StatValue>{stats ? totalTime(stats.minutes) : "–"}</StatValue>
            <StatNote>{stats ? plural(stats.sessions, "session") : ""}</StatNote>
          </Stat>
          <Stat>
            <StatLabel>Decks</StatLabel>
            <StatValue>{stats ? stats.decks : "–"}</StatValue>
            <StatNote>Presented this month</StatNote>
          </Stat>
        </Stats>
      )}

      <SectionTitle>
        Recent presentations
        <Link to="/history">See all</Link>
      </SectionTitle>
      {current && recent.length === 0 && (
        <Card>
          <Muted>
            Nothing yet. Open a deck in Google Slides, click Copresent, and your
            presentations will appear here.
          </Muted>
        </Card>
      )}
      {recent.length > 0 && (
        <Rows>
          {recent.map((p) => (
            <Row key={`${p.deckId}-${p.localDay}`}>
              <RowMain>
                <RowTitle>{p.title || "Untitled deck"}</RowTitle>
                <RowMeta>
                  {dayLabel(p.localDay)} · {durationLabel(presentationMinutes(p))}
                  {p.sessions.length > 1 ? ` · ${plural(p.sessions.length, "session")}` : ""}
                </RowMeta>
              </RowMain>
            </Row>
          ))}
        </Rows>
      )}
    </>
  );
};

// --- Presentations (history) -------------------------------------------------

const MonthNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 8px 0 12px;
  h2 {
    margin: 0;
    font-size: 17px;
    color: var(--text-headline);
  }
`;

const MonthButton = styled.button`
  border: 1px solid var(--border-soft);
  background: transparent;
  color: var(--text-primary);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const AllowanceHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 15px;
  color: var(--text-headline);
  strong {
    font-size: 22px;
    font-variant-numeric: tabular-nums;
  }
`;

const Bar = styled.div`
  margin: 12px 0 10px;
  height: 8px;
  border-radius: 999px;
  background: var(--border-soft);
  overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $full: boolean }>`
  width: ${(p) => p.$pct}%;
  height: 100%;
  border-radius: inherit;
  background: ${(p) => (p.$full ? "#f59e0b" : "#22c55e")};
`;

const Tag = styled.span<{ $tone: "counted" | "free" | "over" }>`
  flex: none;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  color: ${(p) => (p.$tone === "over" ? "#b45309" : "var(--text-muted)")};
  border: 1px solid var(--border-soft);
`;

const SessionLines = styled.div`
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;

const HistoryRow = ({
  presentation,
  showCounting,
}: {
  presentation: UsagePresentation;
  showCounting: boolean;
}) => {
  const tag = !showCounting
    ? null
    : presentation.overLimit
    ? { tone: "over" as const, text: "Over limit" }
    : presentation.counted
    ? { tone: "counted" as const, text: `${presentation.number}` }
    : { tone: "free" as const, text: "Not counted" };
  return (
    <Row>
      <RowMain>
        <RowTitle>{presentation.title || "Untitled deck"}</RowTitle>
        <SessionLines>
          {dayLabel(presentation.localDay)}
          {presentation.sessions.map((s, i) => (
            <div key={i}>
              {timeLabel(s.startedAt)} · {durationLabel(minutesBetween(s.startedAt, s.lastSeenAt))}
              {s.totalSlides > 0 && s.maxSlide > 0
                ? ` · reached slide ${Math.min(s.maxSlide, s.totalSlides)} of ${s.totalSlides}`
                : ""}
            </div>
          ))}
        </SessionLines>
      </RowMain>
      {tag && (
        <Tag
          $tone={tag.tone}
          title={
            tag.tone === "counted"
              ? `Presentation ${presentation.number} of the month`
              : tag.tone === "free"
              ? presentation.brief
                ? "Only opened briefly, so it doesn't count"
                : "Given on Pro, so it doesn't count"
              : undefined
          }
        >
          {tag.text}
        </Tag>
      )}
    </Row>
  );
};

const PresentationsView = () => {
  const current = useMemo(() => monthKey(new Date()), []);
  const [month, setMonth] = useState(current);
  const [report, setReport] = useState<UsageReport | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">("loading");

  const load = useCallback(async (m: string) => {
    setState("loading");
    const result = await fetchUsage(m);
    if (typeof result === "string") {
      setState("unavailable");
      return;
    }
    setReport(result);
    setState("ready");
  }, []);

  useEffect(() => {
    void load(month);
  }, [month, load]);

  const allowance = report?.allowance ?? null;
  // Only a Free account whose limit is being enforced is told about counting.
  const showCounting = !!allowance?.enforced;
  const resetDate = formatResetDate(allowance?.resetsAt);

  return (
    <>
      <Title>History</Title>
      <Lead>Every presentation where the Copresent remote was open.</Lead>

      {showCounting && allowance && month === current && (
        <Card aria-label="Free presentations this month" style={{ marginBottom: 20 }}>
          <AllowanceHead>
            <span>Free presentations left this month</span>
            <strong>
              {allowance.remaining} of {allowance.limit}
            </strong>
          </AllowanceHead>
          <Bar>
            <BarFill
              $pct={Math.min(100, (allowance.used / Math.max(1, allowance.limit)) * 100)}
              $full={allowance.remaining === 0}
            />
          </Bar>
          <Muted>
            Each deck counts once a day, however many times you present it.
            {resetDate ? ` Resets on ${resetDate}.` : ""}{" "}
            <Link to="/billing">Pro has no limit.</Link>
          </Muted>
        </Card>
      )}

      <MonthNav>
        <MonthButton
          type="button"
          onClick={() => setMonth((m) => shiftMonth(m, -1))}
          disabled={month <= FIRST_MONTH}
          aria-label="Previous month"
        >
          ←
        </MonthButton>
        <h2>{monthLabel(month)}</h2>
        <MonthButton
          type="button"
          onClick={() => setMonth((m) => shiftMonth(m, 1))}
          disabled={month >= current}
          aria-label="Next month"
        >
          →
        </MonthButton>
      </MonthNav>

      {state === "loading" && <Muted>Loading…</Muted>}
      {state === "unavailable" && (
        <Card>
          <Muted>We couldn't load your presentations just now.</Muted>
          <Actions>
            <Button type="button" onClick={() => void load(month)}>
              Try again
            </Button>
          </Actions>
        </Card>
      )}
      {state === "ready" && report && report.presentations.length === 0 && (
        <Muted>
          No presentations this month.
          {month === FIRST_MONTH ? " History starts on 26 September 2026." : ""}
        </Muted>
      )}
      {state === "ready" && report && report.presentations.length > 0 && (
        <Rows>
          {report.presentations.map((p) => (
            <HistoryRow
              key={`${p.deckId}-${p.localDay}`}
              presentation={p}
              showCounting={showCounting}
            />
          ))}
        </Rows>
      )}
    </>
  );
};

// --- Billing -------------------------------------------------------------------

const PlanName = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--text-headline);
`;

const Warning = styled.p`
  margin: 10px 0 0;
  font-size: 14px;
  color: #b45309;
`;

const longDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "long" }) : null;

// Two columns, Free and Pro, with the current one marked. Every action is a
// Stripe page (checkout, or the portal opened on one task) that comes back
// here, so no card or plan change is handled in this app.

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PlanColumn = styled.section<{ $current: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 22px 22px 20px;
  border-radius: 16px;
  border: ${(p) => (p.$current ? "2px solid var(--link)" : "1px solid var(--border-soft)")};
  background: var(--bg-card);
`;

const PlanHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CurrentTag = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  background: var(--link);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
`;

const Price = styled.div`
  margin-top: 10px;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--text-headline);

  small {
    margin-left: 4px;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0;
    color: var(--text-muted);
  }
`;

const Features = styled.ul`
  flex: 1;
  margin: 16px 0 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding: 5px 0 5px 22px;
    font-size: 14px;
    line-height: 1.45;
    color: var(--text-primary);
  }

  li::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: var(--link);
    font-weight: 700;
  }
`;

const PlanFoot = styled.div`
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;

  ${Muted} {
    font-size: 13px;
  }
`;

const LinkButton = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--link);
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const BillingFoot = styled.div`
  margin-top: 16px;
`;

const FREE_FEATURES = [
  "Your phone as the slide remote, with notes",
  "Live slide previews, timer and audience Q&A",
  "5 presentations a month",
  "3 presentations with copresenters",
];

const PRO_FEATURES = ["Everything in Free", "Unlimited presentations", "Unlimited copresenting"];

type PortalTask = "cancel" | "card" | "manage";

export const BillingView = () => {
  const posthog = usePostHog();
  const [account, setAccount] = useState<Account | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">("loading");
  const [busy, setBusy] = useState<PortalTask | null>(null);
  const [portalError, setPortalError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchAccount(controller.signal)
      .then((result) => {
        setAccount(result);
        setState("ready");
      })
      .catch(() => {
        if (!controller.signal.aborted) setState("unavailable");
      });
    return () => controller.abort();
  }, []);

  const openPortal = async (task: PortalTask) => {
    setBusy(task);
    setPortalError(false);
    posthog?.capture("billing_portal_opened", { source: "dashboard", task });
    const result = await openBillingPortal({
      returnTo: "billing",
      ...(task === "manage" ? {} : { flow: task }),
    });
    if (result.ok) {
      window.location.assign(result.url);
      return;
    }
    setPortalError(true);
    setBusy(null);
  };

  const subscription = account?.subscription ?? null;
  const lifetime = account?.billing === "lifetime";
  const paid = account?.planSource === "paid";
  const onPro = paid;
  const failing = !!subscription && ["past_due", "unpaid", "incomplete"].includes(subscription.status);
  const ending = !!subscription?.cancelAtPeriodEnd;
  const endsOn = longDate(subscription?.paidThrough ?? subscription?.currentPeriodEnd);
  const canManage = !!account?.canManageBilling;
  // Switching to Free is cancelling the subscription, at the end of what's
  // been paid for. Lifetime has nothing to cancel.
  const canSwitchToFree = paid && !lifetime && !!subscription && !ending && canManage;
  // A monthly subscriber is shown what they pay, not the yearly rate.
  const monthly = paid && subscription?.interval === "month";

  let proStatus: string | null = null;
  if (lifetime) proStatus = "Lifetime. There's nothing to renew.";
  else if (paid && ending) proStatus = endsOn ? `Ends on ${endsOn}. You then move to Free.` : "Ends at the end of this period.";
  else if (paid && subscription?.currentPeriodEnd) {
    const interval = subscription.interval === "year" ? "Yearly" : "Monthly";
    proStatus = `${interval}. Renews on ${longDate(subscription.currentPeriodEnd)}.`;
  }

  return (
    <>
      <Title style={{ marginBottom: 20 }}>Billing</Title>

      {state === "loading" && <Muted>Loading…</Muted>}
      {state === "unavailable" && (
        <Card>
          <Muted>We couldn't load your billing details just now. Try again in a moment.</Muted>
        </Card>
      )}
      {state === "ready" && account && (
        <>
          <PlanGrid>
            <PlanColumn $current={!onPro} aria-label="Free plan">
              <PlanHead>
                <PlanName>Free</PlanName>
                {!onPro && <CurrentTag>Current plan</CurrentTag>}
              </PlanHead>
              <Price>$0</Price>
              <Features>
                {FREE_FEATURES.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </Features>
              {canSwitchToFree && (
                <PlanFoot>
                  <Button type="button" $secondary onClick={() => void openPortal("cancel")} disabled={!!busy}>
                    {busy === "cancel" ? "Opening…" : "Switch to Free"}
                  </Button>
                  <Muted>Pro stays on until the end of what you've paid for.</Muted>
                </PlanFoot>
              )}
            </PlanColumn>

            <PlanColumn $current={onPro} aria-label="Pro plan">
              <PlanHead>
                <PlanName>Pro</PlanName>
                {onPro && <CurrentTag>Current plan</CurrentTag>}
              </PlanHead>
              <Price>
                {monthly ? PRICES.month.amount : PRICES.year.perMonth}
                <small>/ month</small>
              </Price>
              <Muted style={{ marginTop: 2, fontSize: 13 }}>
                {monthly
                  ? `Billed monthly, or ${PRICES.year.amount} yearly`
                  : `Billed ${PRICES.year.amount} yearly, or ${PRICES.month.amount} monthly`}
              </Muted>
              <Features>
                {PRO_FEATURES.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </Features>
              <PlanFoot>
                {proStatus && <Muted>{proStatus}</Muted>}
                {failing && <Warning style={{ margin: 0 }}>Your last payment didn't go through. Update your card to keep Pro.</Warning>}
                {!onPro && (
                  <ButtonLink to="/upgrade?source=dashboard_billing">Upgrade to Pro</ButtonLink>
                )}
                {paid && ending && canManage && (
                  <Button type="button" onClick={() => void openPortal("manage")} disabled={!!busy}>
                    {busy === "manage" ? "Opening…" : "Keep Pro"}
                  </Button>
                )}
                {paid && !lifetime && !ending && canManage && (
                  <Button type="button" $secondary={!failing} onClick={() => void openPortal("card")} disabled={!!busy}>
                    {busy === "card" ? "Opening…" : "Update card"}
                  </Button>
                )}
              </PlanFoot>
            </PlanColumn>
          </PlanGrid>

          {canManage && (
            <BillingFoot>
              <LinkButton type="button" onClick={() => void openPortal("manage")} disabled={!!busy}>
                Invoices and billing history
              </LinkButton>
            </BillingFoot>
          )}
          {portalError && (
            <Warning>We couldn't open billing. Please try again, or email hello@copresent.app.</Warning>
          )}
        </>
      )}
    </>
  );
};

// --- Profile -------------------------------------------------------------------

const ProfileHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Field = styled.label`
  display: block;
  margin-top: 4px;
  font-size: 13px;
  color: var(--text-muted);
`;

const Input = styled.input`
  display: block;
  box-sizing: border-box;
  width: 100%;
  max-width: 360px;
  margin-top: 6px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--border-soft);
  background: var(--bg-page);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 15px;
`;

const Segmented = styled.div`
  display: inline-flex;
  margin-top: 8px;
  padding: 3px;
  border-radius: 10px;
  background: var(--bg-chip);
`;

const Segment = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$active ? "var(--bg-card)" : "transparent")};
  color: ${(p) => (p.$active ? "var(--text-headline)" : "var(--text-muted)")};
`;

const ProfileView = ({
  profile,
  dark,
  onThemeChange,
}: {
  profile: { email: string; name: string; picture: string };
  dark: boolean;
  onThemeChange: (dark: boolean) => void;
}) => {
  const posthog = usePostHog();
  const [displayName, setDisplayName] = useState(
    () => localStorage.getItem("presenterName") || profile.name || ""
  );
  const [saved, setSaved] = useState(false);

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    const value = displayName.trim();
    if (!value) return;
    // The name the host controller joins with (controller.tsx sendHostJoin).
    localStorage.setItem("presenterName", value);
    posthog?.capture("profile_name_changed", { source: "dashboard" });
    setSaved(true);
  };

  const signOut = () => {
    posthog?.capture("host_sign_out_clicked", { source: "dashboard" });
    clearHostSession();
    window.location.assign("/");
  };

  return (
    <>
      <Title>Profile</Title>
      <Lead>How you appear when you present, and how Copresent looks.</Lead>

      <Card>
        <ProfileHead>
          <ProfileAvatar picture={profile.picture} label={profile.name || profile.email} size={48} />
          <div>
            <PlanName style={{ fontSize: 17 }}>{profile.name || profile.email}</PlanName>
            <Muted>{profile.email}</Muted>
          </div>
        </ProfileHead>
        <Muted style={{ marginTop: 14, fontSize: 13 }}>
          Your name, email and photo come from your Google account.{" "}
          <a href="https://myaccount.google.com/personal-info" target="_blank" rel="noreferrer">
            Change them on Google
          </a>
          .
        </Muted>
      </Card>

      <SectionTitle>Presenting</SectionTitle>
      <Card>
        <form onSubmit={save}>
          <Field htmlFor="display-name">Name shown to your copresenters</Field>
          <Input
            id="display-name"
            value={displayName}
            maxLength={60}
            onChange={(e) => {
              setDisplayName(e.target.value);
              setSaved(false);
            }}
          />
          <Actions>
            <Button type="submit" disabled={!displayName.trim()}>
              Save name
            </Button>
            {saved && <Muted style={{ alignSelf: "center" }}>Saved. Used from your next presentation.</Muted>}
          </Actions>
        </form>
      </Card>

      <SectionTitle>Appearance</SectionTitle>
      <Card>
        <Field as="div">Theme</Field>
        <Segmented role="group" aria-label="Theme">
          <Segment type="button" $active={!dark} aria-pressed={!dark} onClick={() => onThemeChange(false)}>
            Light
          </Segment>
          <Segment type="button" $active={dark} aria-pressed={dark} onClick={() => onThemeChange(true)}>
            Dark
          </Segment>
        </Segmented>
      </Card>

      <SectionTitle>Account</SectionTitle>
      <Card>
        <Muted>Sign out of Copresent on this browser.</Muted>
        <Actions>
          <Button type="button" $secondary onClick={signOut}>
            Sign out
          </Button>
        </Actions>
      </Card>
    </>
  );
};

// --- Page ------------------------------------------------------------------------

const SignInCard = ({ returnTo }: { returnTo: string }) => (
  <>
    <Title>Your Copresent dashboard</Title>
    <Lead>Sign in with the Google account you present with.</Lead>
    <Card>
      <Muted>See what's live, your presentation history, billing and profile.</Muted>
      <Actions>
        <Button type="button" onClick={() => void beginGoogleSignIn(returnTo)}>
          Sign in with Google
        </Button>
        <ButtonLink to="/join" $secondary>
          Join a presentation instead
        </ButtonLink>
      </Actions>
    </Card>
  </>
);

// --- Plan card ---------------------------------------------------------------

// The phone Home page's "what am I on, and how much is left", so a Free
// host never meets the limit as a surprise. The counter appears only while the
// limit is ENFORCED: nothing on screen may describe a limit that isn't being
// applied (the controller's usage line follows the same rule).
const PlanBox = styled.div<{ $mobile?: boolean }>`
  margin: 12px 0 4px;
  padding: 14px 12px 12px;
  border-top: 1px solid var(--border-soft);
  display: ${(p) => (p.$mobile ? "none" : "block")};

  @media (max-width: 767px) {
    display: ${(p) => (p.$mobile ? "block" : "none")};
    margin: 0 0 20px;
    padding: 16px;
    border: 1px solid var(--border-soft);
    border-radius: 14px;
    background: var(--bg-card);
  }
`;

const PlanLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--link);
`;

const PlanInfo = styled.span`
  display: inline-flex;
  color: var(--text-muted);
  cursor: help;
`;

const PlanCount = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
`;

const PlanBar = styled.div`
  margin: 8px 0 12px;
  height: 8px;
  border-radius: 999px;
  background: var(--bg-chip);
  border: 1px solid var(--border-soft);
  overflow: hidden;
`;

const PlanBarFill = styled.div<{ $pct: number; $tone: "ok" | "low" | "out" }>`
  width: ${(p) => p.$pct}%;
  min-width: ${(p) => (p.$pct > 0 ? "8px" : "0")};
  height: 100%;
  border-radius: inherit;
  background: ${(p) => (p.$tone === "out" ? "#ef4444" : p.$tone === "low" ? "#f59e0b" : "var(--link)")};
`;

const PlanReset = styled.div`
  margin: -4px 0 12px;
  font-size: 12px;
  color: var(--text-muted);
`;

const PlanButton = styled(ButtonLink)`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  font-size: 13px;
`;

const SidebarUpgrade = styled(PlanButton)`
  @media (max-width: 767px) {
    display: none;
  }
`;

const InfoIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" strokeLinecap="round" />
  </svg>
);

const usePlanSummary = (signedIn: boolean) => {
  const [report, setReport] = useState<UsageReport | null>(null);
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    const load = async () => {
      const result = await fetchUsage(monthKey(new Date()));
      if (!cancelled && typeof result !== "string") setReport(result);
    };
    void load();
    // A presentation in another tab spends one; coming back should show it.
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, [signedIn]);
  return report;
};

export const PlanCard = ({ report, mobile }: { report: UsageReport | null; mobile?: boolean }) => {
  if (!report) return null;
  const allowance = report.allowance?.enforced ? report.allowance : null;
  const name = report.plan === "paid" ? "Pro" : "Free";
  const resetDate = formatResetDate(allowance?.resetsAt);
  const info = allowance
    ? `Free includes ${allowance.limit} presentations a month. Each deck counts once a day, however many times you present it, and opening the remote for under 2 minutes doesn't count.${
        resetDate ? ` Resets on ${resetDate}.` : ""
      } Pro has no limit.`
    : report.plan === "free"
    ? "Pro adds unlimited presentations and copresenting."
    : "Unlimited presentations and copresenting.";
  const remaining = allowance ? Math.max(0, allowance.remaining) : 0;
  const pct = allowance ? Math.min(100, (remaining / Math.max(1, allowance.limit)) * 100) : 0;
  const tone = !allowance ? "ok" : remaining === 0 ? "out" : remaining <= 1 ? "low" : "ok";

  return (
    <PlanBox $mobile={mobile} aria-label="Your plan">
      <PlanLine>
        Current plan: {name}
        <PlanInfo title={info} aria-label={info} role="img">
          <InfoIcon />
        </PlanInfo>
      </PlanLine>
      {allowance ? (
        <>
          <PlanCount>
            {remaining} of {allowance.limit} free presentation{allowance.limit === 1 ? "" : "s"} left
          </PlanCount>
          <PlanBar
            role="meter"
            aria-label="Free presentations left this month"
            aria-valuemin={0}
            aria-valuemax={allowance.limit}
            aria-valuenow={remaining}
          >
            <PlanBarFill $pct={pct} $tone={tone} />
          </PlanBar>
          {remaining <= 1 && resetDate && <PlanReset>Resets on {resetDate}</PlanReset>}
        </>
      ) : (
        report.plan !== "paid" && <div style={{ height: 12 }} aria-hidden="true" />
      )}
      {report.plan !== "paid" && (
        <PlanButton to={`/upgrade?source=${mobile ? "dashboard_plan_mobile" : "dashboard_sidebar"}`} $secondary>
          Upgrade to Pro
        </PlanButton>
      )}
    </PlanBox>
  );
};

const HostDashboard = ({ section }: { section: DashboardSection }) => {
  const posthog = usePostHog();
  const [dark, setDark] = useState(() => readStoredTheme() === "dark");
  const session = getStoredHostSession();
  const profile = session?.profile;

  useEffect(() => {
    posthog?.capture("dashboard_viewed", { section, signed_in: !!session });
    // Once per section visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posthog, section]);

  const changeTheme = (next: boolean) => {
    setDark(next);
    try {
      localStorage.setItem(THEME_KEY, next ? "dark" : "light");
    } catch {
      // Private mode: the choice lasts for this page only.
    }
  };

  const plan = usePlanSummary(!!profile);
  const label = profile?.name || profile?.email || "";
  const firstName = (profile?.name || "").split(" ")[0] || "";

  let view: React.ReactNode;
  if (!profile) {
    view = <SignInCard returnTo={NAV.find((n) => n.id === section)?.to ?? "/"} />;
  } else if (section === "history") {
    view = <PresentationsView />;
  } else if (section === "billing") {
    view = <BillingView />;
  } else if (section === "profile") {
    view = <ProfileView profile={profile} dark={dark} onThemeChange={changeTheme} />;
  } else {
    view = <HomeView firstName={firstName} plan={<PlanCard report={plan} mobile />} />;
  }

  return (
    <Page $dark={dark}>
      <Sidebar aria-label="Dashboard">
        <Brand to="/">
          <img src={logo} alt="" />
          <span>Copresent</span>
        </Brand>
        {NAV.map(({ id, label: text, to, Icon }) => (
          <NavItem key={id} to={to} $active={section === id} aria-current={section === id ? "page" : undefined}>
            <Icon />
            {text}
          </NavItem>
        ))}
        {profile && (
          <SidebarFoot>
            {plan && plan.plan !== "paid" && (
              <SidebarUpgrade to="/upgrade?source=dashboard_sidebar" $secondary>
                Upgrade to Pro
              </SidebarUpgrade>
            )}
            <NavFoot title={profile.email}>
              <ProfileAvatar picture={profile.picture} label={label} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {label}
              </span>
            </NavFoot>
          </SidebarFoot>
        )}
      </Sidebar>
      <Main>
        <Content>{view}</Content>
      </Main>
    </Page>
  );
};

export default HostDashboard;
